import {
  ArrowRight,
  Bot,
  CalendarClock,
  Check,
  Code,
  ConciergeBell,
  Cpu,
  Database,
  Eye,
  Gauge,
  GitPullRequest,
  Hand,
  Headphones,
  History,
  Inbox,
  Layers,
  LayoutDashboard,
  Megaphone,
  Menu,
  MessagesSquare,
  Moon,
  Play,
  Plug,
  Radio,
  Server,
  ShieldCheck,
  Sparkles,
  Store,
  Sun,
  TrendingUp,
  Users,
  Webhook,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { VickiBuilder } from '@/components/landing/vicki-builder';
import { Desklings } from '@/components/landing/desklings';
import { AgentAvatar } from '@/components/agent-avatar';
import { LiveDesk, usePrefersReducedMotion } from '@/components/landing/live-desk';
import { RequestAccess } from '@/components/landing/request-access';
import { WorkspacesShowcase } from '@/components/landing/workspaces-showcase';
import '@/components/landing/scroll-fx.css';
import { LanguageSwitch } from '@/components/language-switch';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { t, tr } from '@/lib/i18n';
import { appUrl } from '@/lib/site';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

const NAV = [
  { id: 'vicki', label: 'VICKI' },
  { id: 'video', label: t('Video') },
  { id: 'how', label: t('How it works') },
  { id: 'workspaces', label: t('Workspaces') },
  { id: 'control', label: t('Control') },
  { id: 'features', label: t('Features') },
  { id: 'use-cases', label: t('Use cases') },
];

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Public start page for visitors who are not signed in. Signed-in people go straight to their desks (router.tsx). */
export function LandingPage() {
  return (
    <div className="min-h-full bg-background text-foreground">
      <LandingNav />
      <main>
        <Hero />
        <MeetVicki />
        <ProductVideo />
        <Shift />
        <HowItWorks />
        <MeetTheDesklings />
        <Workspaces />
        <Control />
        <Features />
        <Architecture />
        <UseCases />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}

/* ───────────────────────────── Nav ───────────────────────────── */

function LandingNav() {
  const { resolved, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id: string) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header className={cn('sticky top-0 z-40 border-b transition-colors', scrolled || open ? 'border-border bg-background/80 backdrop-blur-lg' : 'border-transparent')}>
      <div aria-hidden className="fx-progress absolute inset-x-0 -bottom-px h-0.5 bg-gradient-to-r from-primary via-violet-500 to-primary" />
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 sm:px-6">
        <a href="/" className="flex items-center gap-2 font-semibold tracking-tight">
          <Logo className="size-7" /> Deskworks
        </a>
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => (
            <button key={n.id} type="button" onClick={() => go(n.id)} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              {n.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <LanguageSwitch />
          <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label={resolved === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}>
            {resolved === 'dark' ? <Sun /> : <Moon />}
          </Button>
          <Button asChild size="sm" className="hidden sm:inline-flex">
            <a href={appUrl('/login')}>
              {t('Sign in')} <ArrowRight />
            </a>
          </Button>
          <Button variant="ghost" size="icon-sm" className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label={t('Menu')} aria-expanded={open}>
            {open ? <X /> : <Menu />}
          </Button>
        </div>
      </div>
      {open && (
        <nav className="grid gap-1 border-t px-4 py-3 md:hidden">
          {NAV.map((n) => (
            <button key={n.id} type="button" onClick={() => go(n.id)} className="rounded-md px-3 py-2 text-left text-sm hover:bg-muted">
              {n.label}
            </button>
          ))}
          <Button asChild className="mt-2">
            <a href={appUrl('/login')}>
              {t('Sign in')} <ArrowRight />
            </a>
          </Button>
        </nav>
      )}
    </header>
  );
}

/* ───────────────────────────── Building blocks ───────────────────────────── */

/** True once the element has scrolled into view; stays true. */
function useInView<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return setShown(true);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

/** Brings a block in out of a blur once it scrolls into view. */
function Reveal({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const [ref, shown] = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        'transition-all duration-700 ease-out motion-reduce:transition-none',
        shown ? 'translate-y-0 opacity-100 blur-none' : 'translate-y-6 opacity-0 blur-[6px] motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:blur-none',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A headline whose words rise out of a blur one after the other once it scrolls into view. */
function Words({ text }: { text: string }) {
  const [ref, shown] = useInView<HTMLSpanElement>();
  const words = text.split(' ');
  return (
    <span ref={ref}>
      {words.map((w, i) => (
        <Fragment key={i}>
          <span
            style={{ transitionDelay: `${i * 50}ms` }}
            className={cn(
              'inline-block transition-all duration-700 ease-out motion-reduce:transition-none',
              shown ? 'translate-y-0 opacity-100 blur-none' : 'translate-y-3 opacity-0 blur-[6px] motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:blur-none',
            )}
          >
            {w}
          </span>
          {i < words.length - 1 && ' '}
        </Fragment>
      ))}
    </span>
  );
}

function Section({ id, className, children }: { id?: string; className?: string; children: ReactNode }) {
  return (
    <section id={id} className={cn('scroll-mt-16 px-4 py-20 sm:px-6 sm:py-28', className)}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="mb-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-primary">{children}</div>;
}

function SectionHead({ eyebrow, title, children, center }: { eyebrow: string; title: ReactNode; children?: ReactNode; center?: boolean }) {
  return (
    <Reveal className={cn('mb-12 max-w-2xl sm:mb-16', center && 'mx-auto text-center')}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{typeof title === 'string' ? <Words text={title} /> : title}</h2>
      {children && <p className="mt-4 text-base text-pretty text-muted-foreground sm:text-lg">{children}</p>}
    </Reveal>
  );
}

function IconTile({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return (
    <div className={cn('flex size-10 items-center justify-center rounded-xl border bg-card text-primary shadow-xs', className)}>
      <Icon className="size-5" />
    </div>
  );
}

/* ───────────────────────────── Hero ───────────────────────────── */

function Hero() {
  return (
    <section className="relative overflow-hidden px-4 pt-12 pb-20 sm:px-6 sm:pt-20 sm:pb-28">
      {/* Backdrop: soft grid + glow */}
      <div aria-hidden className="fx-parallax pointer-events-none absolute inset-0 -z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:56px_56px] opacity-50 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
        <div className="absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl dark:bg-primary/15" />
        <div className="absolute top-40 -right-40 h-[360px] w-[360px] rounded-full bg-violet-500/15 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-[1.05fr_1fr] lg:gap-12">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-card/70 py-1 pr-3 pl-1 text-xs backdrop-blur">
            <span className="rounded-full bg-primary px-2 py-0.5 font-semibold text-primary-foreground">{t('New')}</span>
            <span className="text-muted-foreground">{t('VICKI at the Front Desk: say it, she builds it')}</span>
          </div>
          <h1 className="text-4xl leading-[1.05] font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
            {t('Tell VICKI what you want.')}{' '}
            <span className="bg-gradient-to-r from-primary via-violet-500 to-primary bg-clip-text text-transparent">{t('She builds the desks that do it.')}</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-pretty text-muted-foreground">
            {t('VICKI is your Front Desk. She understands your business, proposes the desks you need, builds them once you say yes, hands them the work and brings you the results. You only talk to her.')}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <RequestAccess>
              <Button size="lg" className="h-11 px-6 text-[15px]">
                {t('Request access')} <ArrowRight />
              </Button>
            </RequestAccess>
            <Button size="lg" variant="outline" className="h-11 px-6 text-[15px]" onClick={() => scrollToSection('vicki')}>
              <ConciergeBell /> {t('Meet VICKI')}
            </Button>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            {[t('One chat for the whole business'), t('You decide what she does alone'), t('Your Claude or ChatGPT subscription')].map((point) => (
              <li key={point} className="inline-flex items-center gap-1.5">
                <Check className="size-4 text-primary" /> {point}
              </li>
            ))}
          </ul>
        </div>

        <div className="animate-in fade-in slide-in-from-bottom-6 delay-150 duration-1000 fill-mode-both">
          <HeroDemo />
        </div>
      </div>
    </section>
  );
}

type DemoPhase = 'build' | 'run';

const DEMO_PHASES: { id: DemoPhase; label: string; note: [string, string] }[] = [
  { id: 'build', label: t('You tell VICKI'), note: [t('One sentence.'), t('VICKI builds the team.')] },
  { id: 'run', label: t('The desks run'), note: [t('0 tabs open.'), t('The desks do the clicking.')] },
];

/** Hero demo in two acts, looping: VICKI builds desks from one sentence at the Front Desk, then a desk works. */
function HeroDemo() {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState<DemoPhase>('build');
  // Bumped on every phase change, so a replayed act starts from the beginning.
  const [round, setRound] = useState(0);
  const show = (p: DemoPhase) => {
    setPhase(p);
    setRound((r) => r + 1);
  };
  const note = DEMO_PHASES.find((p) => p.id === phase)!.note;

  return (
    <div className="relative mx-auto max-w-lg lg:max-w-none">
      <div className="mb-3 flex items-center justify-center gap-1 lg:justify-start" role="tablist" aria-label={t('Demo')}>
        {DEMO_PHASES.map((p, i) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={phase === p.id}
            onClick={() => show(p.id)}
            className={cn(
              'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium transition-colors',
              phase === p.id ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <span className={cn('flex size-4 items-center justify-center rounded-full text-[10px]', phase === p.id ? 'bg-background/20' : 'border')}>{i + 1}</span>
            {p.label}
          </button>
        ))}
      </div>
      <div className="min-h-[520px]">
        <div key={`${phase}-${round}`} className="animate-in fade-in zoom-in-[0.98] duration-500">
          {phase === 'build' ? (
            <VickiBuilder still={reduced} onDone={() => show('run')} />
          ) : (
            <LiveDesk onLoopEnd={reduced ? undefined : () => show('build')} />
          )}
        </div>
      </div>
      <div key={phase} className="animate-in fade-in pointer-events-none absolute -top-2 -right-3 hidden rotate-[3deg] rounded-xl border bg-card px-3 py-2 text-xs shadow-lg duration-700 lg:block">
        <span className="font-medium">{note[0]}</span> <span className="text-muted-foreground">{note[1]}</span>
      </div>
    </div>
  );
}

/* ───────────────────────────── Meet VICKI ───────────────────────────── */

const VICKI_FLOW: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: MessagesSquare, title: t('You say what you want'), body: t('“Take care of our customer cases.” “Get us more reviews.” In your words, in one chat for the whole business.') },
  { icon: Sparkles, title: t('VICKI proposes the desks'), body: t('She looks at what you have, asks what she needs and proposes the teams: who leads, who works, which tools, which goals.') },
  { icon: Hand, title: t('You say yes, she builds'), body: t('One click and she sets up workspaces, desks, agents and connections. Or she just starts, if you let her.') },
  { icon: TrendingUp, title: t('The desks work, she reports'), body: t('She hands the work to the desks, keeps an eye on everything and brings results and questions back into your chat.') },
];

/** Her rights (what she may do), then her mode (who starts). */
const VICKI_MODES: { name: string; body: string }[] = [
  { name: t('Read'), body: t('She reads everything and proposes, you decide every step.') },
  { name: t('Read/Write'), body: t('She changes things herself and tells you what she did.') },
  { name: t('Guided'), body: t('She does what you tell her.') },
  { name: t('God mode'), body: t('She starts on her own and does what serves your goals.') },
];

function MeetVicki() {
  return (
    <Section id="vicki" className="border-t bg-gradient-to-b from-primary/5 via-transparent to-transparent">
      <SectionHead eyebrow={t('Meet VICKI')} title={t('One Front Desk for your whole business.')} center>
        {t('You don’t set up teams, agents and tools anymore. You talk to VICKI. She builds and runs the desks under the hood, and grows them with your business.')}
      </SectionHead>
      <ol className="relative grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div aria-hidden className="fx-draw-x absolute top-9 right-[12%] left-[12%] hidden h-px bg-gradient-to-r from-border via-primary/50 to-border lg:block" />
        {VICKI_FLOW.map((s, i) => (
          <Reveal key={s.title} delay={i * 120}>
            <li className="relative flex h-full flex-col rounded-2xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <IconTile icon={s.icon} className="relative bg-background" />
                <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
            </li>
          </Reveal>
        ))}
      </ol>
      <div className="mt-6 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Reveal>
          <div className="h-full rounded-2xl border bg-card p-6">
            <div className="text-sm font-semibold">{t('You decide how much she does on her own')}</div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {VICKI_MODES.map((m, i) => (
                <div key={m.name} className={cn('rounded-xl border px-3 py-2.5', i === 3 && 'border-primary/40 bg-primary/5')}>
                  <div className="text-sm font-medium">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.body}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{t('Her goals and how much she may do stay with you.')}</p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="h-full rounded-2xl border bg-card p-6">
            <div className="text-sm font-semibold">{t('She learns your business')}</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[
                t('Remembers what you tell her: goals, customers, competition, decisions'),
                t('Writes down how she works and gets better every week'),
                t('Does a round every morning: what hangs, what needs you, what she did'),
              ].map((p) => (
                <li key={p} className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" /> <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* ───────────────────────────── Product video ───────────────────────────── */

/** The two-minute product film, front and centre under the hero. It has a voiceover, so it only plays on a click. */
function ProductVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const start = () => {
    setStarted(true);
    void ref.current?.play();
  };

  return (
    <section id="video" className="scroll-mt-16 px-4 pb-20 sm:px-6 sm:pb-28">
      <div className="mx-auto max-w-5xl">
        <div className="fx-tilt relative">
          <div aria-hidden className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-gradient-to-r from-primary/20 via-violet-500/20 to-primary/20 blur-2xl" />
          <div className="relative overflow-hidden rounded-2xl border bg-card shadow-2xl">
            <video
              ref={ref}
              className="aspect-video w-full bg-black"
              src="/video/deskworks-product.mp4"
              poster="/video/deskworks-product-poster.jpg"
              preload="none"
              playsInline
              controls={started}
              onPlay={() => setStarted(true)}
            />
            {!started && (
              <button type="button" onClick={start} aria-label={t('Play the Deskworks video')} className="group absolute inset-0 flex items-center justify-center bg-zinc-950/10 transition-colors hover:bg-zinc-950/20">
                <span className="flex items-center gap-3 rounded-full bg-primary py-2 pr-5 pl-2 text-sm font-medium text-primary-foreground shadow-xl transition-transform group-hover:scale-105 sm:py-3 sm:pr-6 sm:pl-4 sm:text-[15px]">
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary-foreground/20 sm:size-9">
                    <Play className="size-4 fill-current" />
                  </span>
                  <span className="sm:hidden">{t('Watch · 2 min')}</span>
                  <span className="hidden sm:inline">{t('Watch Deskworks in 2 minutes')}</span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────── Shift ───────────────────────────── */

const SHIFT = [
  {
    name: t('Chat assistants'),
    verdict: t('Wait for you'),
    points: [t('You prompt, you copy, you paste'), t('Forgets the context tomorrow'), t('Never acts on its own')],
  },
  {
    name: t('Workflow automation'),
    verdict: t('Breaks on the first exception'),
    points: [t('If-this-then-that, wired by hand'), t('Every edge case is a new branch'), t('No judgement, no questions')],
  },
  {
    name: t('A Deskworks desk'),
    verdict: t('Owns the outcome'),
    points: [t('Decides what work is needed'), t('Asks when unsure, remembers everything'), t('Stops for you before risky steps')],
    highlight: true,
  },
];

function Shift() {
  return (
    <Section className="border-t bg-muted/30">
      <SectionHead eyebrow={t('Not another chatbot')} title={t('Chat assistants wait for you. Desks don’t.')} center>
        {t('A desk is a responsibility you hand over, like the support desk or the news desk. It has a goal, a lead, a team and rules, and it keeps working while you do something else.')}
      </SectionHead>
      <div className="grid gap-4 md:grid-cols-3">
        {SHIFT.map((c, i) => (
          <Reveal key={c.name} delay={i * 100}>
            <div
              className={cn(
                'h-full rounded-2xl border p-6',
                c.highlight ? 'relative border-primary/40 bg-card shadow-xl shadow-primary/10 ring-1 ring-primary/20' : 'bg-card/60',
              )}
            >
              {c.highlight && <div className="absolute -top-3 left-6 rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">Deskworks</div>}
              <div className="text-sm font-medium text-muted-foreground">{c.name}</div>
              <div className={cn('mt-1 text-xl font-semibold tracking-tight', c.highlight && 'text-primary')}>{c.verdict}</div>
              <ul className="mt-5 space-y-2.5 text-sm">
                {c.points.map((p) => (
                  <li key={p} className="flex gap-2">
                    {c.highlight ? <Check className="mt-0.5 size-4 shrink-0 text-primary" /> : <X className="mt-0.5 size-4 shrink-0 text-muted-foreground/60" />}
                    <span className={c.highlight ? '' : 'text-muted-foreground'}>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────────── How it works ───────────────────────────── */

const STEPS: { icon: LucideIcon; title: string; body: string; detail: ReactNode }[] = [
  {
    icon: ConciergeBell,
    title: t('Tell VICKI what you want'),
    body: t('In your own words, at the Front Desk. VICKI asks what she needs and proposes the desks: agents, goals, playbook and triggers. She builds them once you say yes.'),
    detail: <Quote>{t('Every customer case should be analysed and answered within 48 hours.')}</Quote>,
  },
  {
    icon: Radio,
    title: t('The desk wakes up on its own'),
    body: t('Schedules, polling and webhooks wake the desk’s Lead agent. The Lead looks at what happened, decides what work is needed and creates Work Items.'),
    detail: (
      <div className="flex flex-wrap gap-2">
        <Chip icon={CalendarClock}>{t('Mon–Fri 08:00')}</Chip>
        <Chip icon={Radio}>{t('every 5 min')}</Chip>
        <Chip icon={Webhook}>{t('webhook')}</Chip>
      </div>
    ),
  },
  {
    icon: Bot,
    title: t('Agents do the work'),
    body: t('Each Work Item runs in its own worker with exactly the tools the desk allows. Agents split big jobs, review each other and keep going across restarts.'),
    detail: (
      <div className="flex flex-wrap gap-2">
        <Chip icon={Users}>{t('Lead → Support → Engineer')}</Chip>
        <Chip icon={GitPullRequest}>{t('maker / checker')}</Chip>
      </div>
    ),
  },
  {
    icon: Hand,
    title: t('You decide what matters'),
    body: t('Refunds, code changes, emails to customers: they land in your inbox, ready to approve in one click. Everything else just gets done.'),
    detail: (
      <div className="flex flex-wrap gap-2">
        <Chip icon={Inbox} tone="amber">
          {t('{n} need you', { n: 2 })}
        </Chip>
        <Chip icon={Check} tone="green">
          {t('{n} done today', { n: 37 })}
        </Chip>
      </div>
    ),
  },
];

function Quote({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border-l-2 border-primary bg-primary/5 px-3 py-2 text-sm italic">{tr('“{quote}”', { quote: children })}</div>;
}

function Chip({ icon: Icon, children, tone }: { icon: LucideIcon; children: ReactNode; tone?: 'amber' | 'green' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-xs font-medium',
        tone === 'amber' && 'border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300',
        tone === 'green' && 'border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300',
      )}
    >
      <Icon className="size-3.5" />
      {children}
    </span>
  );
}

function HowItWorks() {
  return (
    <Section id="how">
      <SectionHead eyebrow={t('How it works')} title={t('From one sentence to a desk that runs by itself.')}>
        {t('You describe the outcome. Deskworks staffs the desk, wakes it when there is work and brings you in only where you want to be.')}
      </SectionHead>
      <ol className="relative grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div aria-hidden className="fx-draw-x absolute top-5 right-[12%] left-[12%] hidden h-px bg-gradient-to-r from-border via-primary/50 to-border lg:block" />
        {STEPS.map((s, i) => (
          <Reveal key={s.title} delay={i * 120}>
            <li className="relative flex h-full flex-col">
              <div className="flex items-center gap-3">
                <IconTile icon={s.icon} className="relative bg-background" />
                <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
              </div>
              <h3 className="mt-5 text-lg font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.body}</p>
              <div className="mt-5">{s.detail}</div>
            </li>
          </Reveal>
        ))}
      </ol>
    </Section>
  );
}

/* ───────────────────────────── Desklings ───────────────────────────── */

function MeetTheDesklings() {
  return (
    <Section id="desklings" className="bg-gradient-to-b from-transparent via-primary/5 to-transparent">
      <SectionHead eyebrow={t('Meet the Desklings')} title={t('Every desk is a little team.')} center>
        {t('The Desklings are your desk’s AI agents. The Lead with the crown plans the work and decides, the others do it. One look tells you who is working, who is asleep and who is waiting for you.')}
      </SectionHead>
      <Desklings />
    </Section>
  );
}

/* ───────────────────────────── Workspaces ───────────────────────────── */

function Workspaces() {
  return (
    <Section id="workspaces" className="overflow-hidden border-t">
      <SectionHead eyebrow={t('Workspaces')} title={t('A workspace per team. A desk for every job in it.')} center>
        {t('Each team gets a workspace with its own data, connections and runtimes. Inside it, a desk for every job: they run side by side, hand work to each other and stop for you only when it counts.')}
      </SectionHead>
      <Reveal delay={100}>
        <WorkspacesShowcase />
      </Reveal>
    </Section>
  );
}

/* ───────────────────────────── Control ───────────────────────────── */

type Permission = 'allow' | 'ask' | 'deny';

const TOOLS: { tool: string; permission: Permission }[] = [
  { tool: 'shop.get_order', permission: 'allow' },
  { tool: 'helpdesk.add_internal_note', permission: 'allow' },
  { tool: 'helpdesk.reply_to_customer', permission: 'ask' },
  { tool: 'shop.create_refund', permission: 'ask' },
  { tool: 'git.merge_to_main', permission: 'deny' },
];

/** Lowercase on purpose (shown capitalised): the permission names, not the “Ask” mode. */
const PERMISSION_LABEL: Record<Permission, string> = { allow: t('allow'), ask: t('ask'), deny: t('deny') };

const PERMISSION_STYLE: Record<Permission, string> = {
  allow: 'bg-emerald-600 text-white',
  ask: 'bg-amber-500 text-white dark:text-amber-950',
  deny: 'bg-red-600 text-white',
};

function Control() {
  return (
    <Section id="control" className="border-t bg-muted/30">
      <div className="grid items-center gap-14 lg:grid-cols-2 [&>*]:min-w-0">
        <div>
          <SectionHead eyebrow={t('Control')} title={t('Autonomy on a dial, not a leap of faith.')}>
            {t('You decide per desk and per tool what agents may do alone, what needs your OK and what is off limits. Deskworks enforces it on the server, so an agent that ignores its prompt still can’t skip you.')}
          </SectionHead>
          <ul className="-mt-4 space-y-5">
            {[
              { icon: ShieldCheck, title: t('Allow, ask or deny, per tool'), body: t('Every write is an action. “Ask” holds the action until a person decides, for as long as it takes.') },
              { icon: MessagesSquare, title: t('Approval rules in plain words'), body: t('“Analysing the case is fine. Before you write to the customer, ask me.” Written on the Work Item, followed by the agent.') },
              { icon: Eye, title: t('Ask mode or God mode'), body: t('Unsure agents ask you, or pick the most reasonable option and list their assumptions. One switch per desk.') },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 100}>
                <li className="flex gap-4">
                  <IconTile icon={f.icon} className="shrink-0" />
                  <div>
                    <div className="font-semibold">{f.title}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>

        <Reveal delay={150}>
          <div className="rounded-2xl border bg-card p-1.5 shadow-xl shadow-primary/5">
            <div className="rounded-xl border bg-background">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <div className="text-sm font-semibold">{t('Permissions · {desk}', { desk: t('Customer Support') })}</div>
                  <div className="text-xs text-muted-foreground">{t('Checked by Deskworks on every call')}</div>
                </div>
                <div className="inline-flex rounded-md border p-0.5 text-xs">
                  <span className="rounded bg-secondary px-2 py-1 font-medium">{t('Ask')}</span>
                  <span className="px-2 py-1 text-muted-foreground">{t('God mode')}</span>
                </div>
              </div>
              <ul className="divide-y">
                {TOOLS.map((row) => (
                  <li key={row.tool} className="flex items-center gap-3 px-4 py-3">
                    <Plug className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">{row.tool}</span>
                    <div className="inline-flex rounded-md border p-0.5 text-[11px] font-medium">
                      {(['allow', 'ask', 'deny'] as const).map((p) => (
                        <span key={p} className={cn('rounded px-2 py-0.5 capitalize', row.permission === p ? PERMISSION_STYLE[p] : 'text-muted-foreground')}>
                          {PERMISSION_LABEL[p]}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                {tr('{last7Days} 412 actions ran on their own · 9 asked you · 0 denied calls got through', { last7Days: <span className="font-medium text-foreground">{t('Last 7 days:')}</span> })}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* ───────────────────────────── Features ───────────────────────────── */

const FEATURES: { icon: LucideIcon; title: string; body: string; wide?: boolean }[] = [
  { icon: Eye, title: t('Watch it work, live'), body: t('Every step, every tool call, every message as it happens. Chat with an agent mid-task, pause it, send it back.'), wide: true },
  { icon: GitPullRequest, title: t('Agents check each other'), body: t('Maker and checker: an Engineer asks the Architect before touching code. Three rounds, then a human decides.') },
  { icon: Database, title: t('A database per workspace'), body: t('Desks keep their own tables in Postgres. Every desk can read and write; you can browse and query it.') },
  { icon: LayoutDashboard, title: t('Live views from a sentence'), body: t('Describe a dashboard in words. The view assistant builds it, and it updates the moment the data changes.'), wide: true },
  { icon: Plug, title: t('MCP in both directions'), body: t('Connect any MCP server or HTTP API. Or drive Deskworks itself from your own AI tools with a personal token.') },
  { icon: Store, title: t('Desk Store'), body: t('Install a ready-made Support or Engineering desk with agents, playbook, skills and tables in one click.') },
  { icon: Cpu, title: t('Bring your own model'), body: t('Claude and ChatGPT subscriptions, Anthropic and OpenAI API keys, with an automatic fallback. Pick a model per agent.') },
  { icon: Gauge, title: t('Costs in plain sight'), body: t('Tokens and cost per desk, agent and month, plus subscription limits before you hit them.') },
  { icon: History, title: t('Long jobs survive'), body: t('Work runs in segments. A worker can disappear any second; the next one picks up the session where it stopped.'), wide: true },
];

function Features() {
  return (
    <Section id="features">
      <SectionHead eyebrow={t('Features')} title={t('Everything a desk needs to run without you watching.')}>
        {t('And everything you need when you do want to watch.')}
      </SectionHead>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={(i % 4) * 80} className={cn(f.wide && 'lg:col-span-2')}>
            <div className="group relative h-full overflow-hidden rounded-2xl border bg-card p-6 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5">
              <div aria-hidden className="absolute -top-16 -right-16 size-40 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity group-hover:opacity-100" />
              <IconTile icon={f.icon} />
              <h3 className="mt-5 font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────────── Architecture ───────────────────────────── */

const FLOW = [
  { icon: Radio, label: t('Trigger') },
  { icon: Users, label: t('Desk Lead') },
  { icon: Layers, label: t('Work Item') },
  { icon: Server, label: t('Disposable worker') },
  { icon: Check, label: t('Result saved') },
];

function Architecture() {
  return (
    <section className="relative overflow-hidden bg-zinc-950 px-4 py-20 text-zinc-100 sm:px-6 sm:py-28">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgb(255_255_255/0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgb(255_255_255/0.05)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_60%_70%_at_50%_50%,black,transparent)]" />
        <div className="absolute top-1/2 left-1/2 h-[400px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-600/25 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-6xl">
        <Reveal className="mx-auto max-w-2xl text-center">
          <div className="mb-3 text-[13px] font-semibold tracking-[0.14em] text-indigo-300 uppercase">{t('Built to be trusted')}</div>
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-5xl">
            {t('Responsibility persists.')}
            <br />
            <span className="text-zinc-400">{t('Compute is temporary.')}</span>
          </h2>
          <p className="mt-5 text-zinc-400 sm:text-lg">
            {t('Every run is a short-lived worker that gets only an ID and a token that expires. Postgres is the single source of truth. Workers can vanish at any moment, and the work carries on.')}
          </p>
        </Reveal>

        <Reveal delay={150}>
          <div className="mt-14 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
            {FLOW.map((f, i) => (
              <div key={f.label} className="flex items-center gap-3 sm:contents">
                <div style={{ '--fx-i': i * 2 } as CSSProperties} className="fx-step flex flex-1 items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur sm:flex-none">
                  <f.icon className="size-4 text-indigo-300" />
                  <span className="text-sm font-medium whitespace-nowrap">{f.label}</span>
                </div>
                {i < FLOW.length - 1 && <ArrowRight style={{ '--fx-i': i * 2 + 1 } as CSSProperties} className="fx-step hidden size-4 shrink-0 text-zinc-600 sm:block" />}
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-3">
          {[
            { k: t('Isolated'), v: t('Each Work Item runs alone, with only the tools its desk allows.') },
            { k: t('Audited'), v: t('Every action, approval and message is stored. Full transcripts per run.') },
            { k: t('Encrypted'), v: t('API keys and logins are encrypted at rest and never reach the browser.') },
          ].map((s, i) => (
            <Reveal key={s.k} delay={i * 100} className="h-full">
              <div className="h-full bg-zinc-950 p-6">
                <div className="text-lg font-semibold">{s.k}</div>
                <p className="mt-1.5 text-sm text-zinc-400">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────── Use cases ───────────────────────────── */

/** “It asks you …” in bold, the start of each use case’s `asks` sentence. */
const asksYou = <span className="font-semibold">{t('It asks you')}</span>;

const USE_CASES: { id: string; icon: LucideIcon; name: string; goal: string; team: string[]; does: string[]; asks: ReactNode }[] = [
  {
    id: 'support',
    icon: Headphones,
    name: t('Support'),
    goal: t('Every customer case analysed and answered within 48 hours.'),
    team: [t('Lead'), t('Support agent'), t('Engineer')],
    does: [t('Reads new cases from your helpdesk every few minutes'), t('Looks up orders, accounts and logs before answering'), t('Hands real bugs to engineering with a reproduction')],
    asks: tr('{asksYou} before refunds and before the first reply to a customer.', { asksYou }),
  },
  {
    id: 'engineering',
    icon: Code,
    name: t('Engineering'),
    goal: t('Bugs from support are fixed and tested within a week.'),
    team: [t('Lead'), t('Engineer'), t('Architect')],
    does: [t('Reproduces the bug in a coding worker with your repository'), t('Writes the fix and the test, the Architect reviews it'), t('Opens a merge request with a clear summary')],
    asks: tr('{asksYou} before anything is merged.', { asksYou }),
  },
  {
    id: 'sales',
    icon: TrendingUp,
    name: t('Sales'),
    goal: t('Every new lead gets a qualified, personal reply within 2 hours.'),
    team: [t('Lead'), t('Researcher'), t('Writer')],
    does: [t('Researches the company and the person behind each lead'), t('Scores the fit against your ideal customer'), t('Drafts a reply that sounds like you, not like a template')],
    asks: tr('{asksYou} before an email leaves the house.', { asksYou }),
  },
  {
    id: 'marketing',
    icon: Megaphone,
    name: t('Marketing'),
    goal: t('Two blog posts and four LinkedIn posts a week about our product.'),
    team: [t('Lead'), t('Writer'), t('Editor')],
    does: [t('Plans topics from product changes and customer questions'), t('Writes drafts, the Editor checks tone and facts'), t('Tracks what was published in its own table')],
    asks: tr('{asksYou} before anything is published.', { asksYou }),
  },
];

function UseCases() {
  const [active, setActive] = useState(USE_CASES[0]!.id);
  const uc = USE_CASES.find((u) => u.id === active) ?? USE_CASES[0]!;
  return (
    <Section id="use-cases">
      <SectionHead eyebrow={t('Use cases')} title={t('One workspace. A desk for every area of your company.')} center>
        {t('Start with the area that eats the most of your week. Add the next desk when the first one runs.')}
      </SectionHead>

      <div role="tablist" aria-label={t('Use cases')} className="mx-auto mb-8 flex w-fit max-w-full flex-wrap justify-center gap-1 rounded-xl border bg-muted/50 p-1">
        {USE_CASES.map((u) => (
          <button
            key={u.id}
            type="button"
            role="tab"
            aria-selected={u.id === active}
            onClick={() => setActive(u.id)}
            className={cn('inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all', u.id === active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >
            <u.icon className="size-4" /> {u.name}
          </button>
        ))}
      </div>

      <div key={uc.id} role="tabpanel" className="animate-in fade-in slide-in-from-bottom-2 grid gap-4 duration-500 lg:grid-cols-[1fr_1.3fr]">
        <div className="flex flex-col justify-between rounded-2xl border bg-gradient-to-br from-primary/10 via-card to-card p-7">
          <div>
            <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">{t('The goal you write')}</div>
            <p className="mt-3 text-2xl leading-snug font-semibold tracking-tight text-balance">{t('“{quote}”', { quote: uc.goal })}</p>
          </div>
          <div className="mt-8">
            <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">{t('The team you get')}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {uc.team.map((member) => (
                <span key={member} className="inline-flex items-center gap-1.5 rounded-full border bg-card py-1 pr-3 pl-1 text-sm">
                  <AgentAvatar seed={`${uc.id}-${member}`} size={22} className="rounded-full" /> {member}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-7">
          <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">{t('What the desk does')}</div>
          <ul className="mt-4 space-y-3.5">
            {uc.does.map((d) => (
              <li key={d} className="flex gap-3">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Check className="size-3 text-primary" />
                </span>
                <span className="text-[15px]">{d}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 flex items-start gap-3 rounded-xl border border-amber-300/60 bg-amber-50 p-4 text-sm dark:border-amber-800/60 dark:bg-amber-950/30">
            <Hand className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>{uc.asks}</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ───────────────────────────── Final CTA ───────────────────────────── */

function FinalCta() {
  return (
    <section className="px-4 pb-20 sm:px-6 sm:pb-28">
      <Reveal className="mx-auto max-w-6xl">
        <div className="fx-expand relative overflow-hidden rounded-3xl bg-primary px-6 py-16 text-center text-primary-foreground sm:px-12 sm:py-20">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 -left-24 size-80 rounded-full bg-white/15 blur-3xl" />
            <div className="absolute -right-24 -bottom-24 size-80 rounded-full bg-violet-400/30 blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="mx-auto max-w-2xl text-3xl font-semibold tracking-tight text-balance sm:text-5xl">{t('Tell VICKI what you want. Today.')}</h2>
            <p className="mx-auto mt-4 max-w-xl text-pretty opacity-80 sm:text-lg">{t('One sentence is enough to start. VICKI builds the desks, you watch every step, and nothing risky happens without you.')}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" variant="secondary" className="h-11 px-6 text-[15px]">
                <a href={appUrl('/login')}>
                  {t('Sign in')} <ArrowRight />
                </a>
              </Button>
              <Button size="lg" variant="ghost" className="h-11 px-6 text-[15px] text-primary-foreground hover:bg-white/10 hover:text-primary-foreground" onClick={() => scrollToSection('how')}>
                {t('How it works')}
              </Button>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ───────────────────────────── Footer ───────────────────────────── */

function Footer() {
  return (
    <footer className="border-t px-4 py-10 sm:px-6">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Logo className="size-6" />
          <span className="font-semibold">Deskworks</span>
          <span className="text-muted-foreground">· {t('Delegated AI work, under your control.')}</span>
        </div>
        <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
          {NAV.map((n) => (
            <button key={n.id} type="button" onClick={() => scrollToSection(n.id)} className="hover:text-foreground">
              {n.label}
            </button>
          ))}
          <a href={appUrl('/login')} className="hover:text-foreground">
            {t('Sign in')}
          </a>
        </nav>
      </div>
      <div className="mx-auto mt-6 flex max-w-6xl flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <span>© {new Date().getFullYear()} Deskworks · SELLRZ GmbH</span>
        <a href="/privacy" className="hover:text-foreground">
          {t('Privacy')}
        </a>
        <a href="/terms-and-conditions" className="hover:text-foreground">
          {t('Terms')}
        </a>
        <a href="https://sellrz.io/imprint" className="hover:text-foreground">
          {t('Imprint')}
        </a>
      </div>
    </footer>
  );
}
